import { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, Users, Package, ShoppingCart, Calculator, LogOut, Truck, MapPin, FileText, Building2, Settings, ClipboardList, CheckSquare, BarChart3, Menu, X, Wallet, Boxes, Receipt, CreditCard, Lightbulb, Crown, Zap, ShieldCheck, Sparkles, ChevronDown, FileSpreadsheet, Banknote, UsersRound, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/App";
import { toast } from "sonner";
import { useSubscription, tierLabel, tierBadgeClass } from "@/lib/subscription";
import NotificationBell from "@/components/NotificationBell";

// --------------------------------------------------------------------------
// Sidebar groups — single source of truth. Render order is preserved as-is.
// Items can declare:
//   - `feature`: gates against the plan; locked items still render with a
//     crown icon and link to /upgrade.
//   - `toggleable`: if true, the item respects the preferences `modulos`
//     toggle. Defaults to true. Set false for items that must always render
//     (Mi Empresa, Ajustes, Dashboard).
// --------------------------------------------------------------------------
const TOP_LINK = {
  key: "dashboard", to: "/", label: "Dashboard", Icon: LayoutDashboard,
  testid: "nav-dashboard", end: true, toggleable: false,
};

const NAV_GROUPS = [
  {
    key: "ventas", label: "Ventas",
    items: [
      { key: "customers", to: "/customers", label: "Clientes", Icon: Users, testid: "nav-customers" },
      { key: "orders", to: "/orders", label: "Pedidos", Icon: ShoppingCart, testid: "nav-orders" },
      { key: "invoices", to: "/invoices", label: "Facturas", Icon: Receipt, testid: "nav-invoices", feature: "invoicing" },
      { key: "receivables", to: "/receivables", label: "Cuentas por cobrar", Icon: CreditCard, testid: "nav-receivables", feature: "receivables" },
      { key: "returns", to: "/returns", label: "Devoluciones", Icon: Undo2, testid: "nav-returns", feature: "receivables" },
    ],
  },
  {
    key: "operaciones", label: "Operaciones",
    items: [
      { key: "inventory", to: "/inventory", label: "Inventario", Icon: Boxes, testid: "nav-inventory", feature: "inventory" },
      { key: "products", to: "/products", label: "Productos", Icon: Package, testid: "nav-products" },
      { key: "routes", to: "/routes", label: "Rutas", Icon: MapPin, testid: "nav-routes", feature: "route_planning" },
    ],
  },
  {
    key: "compras", label: "Compras",
    items: [
      { key: "suppliers", to: "/suppliers", label: "Proveedores", Icon: Truck, testid: "nav-suppliers" },
      { key: "purchase_orders", to: "/purchase-orders", label: "Órdenes de compra", Icon: ClipboardList, testid: "nav-purchase-orders", feature: "purchase_orders" },
    ],
  },
  {
    key: "finanzas", label: "Finanzas",
    items: [
      { key: "expenses", to: "/expenses", label: "Gastos", Icon: Wallet, testid: "nav-expenses" },
      { key: "costing", to: "/costing", label: "Costeo", Icon: Calculator, testid: "nav-costing", feature: "costing" },
      { key: "documents", to: "/documents", label: "Documentos", Icon: FileText, testid: "nav-documents" },
    ],
  },
  {
    key: "inteligencia", label: "Inteligencia",
    items: [
      { key: "analytics", to: "/analytics", label: "Analítica", Icon: BarChart3, testid: "nav-analytics" },
      { key: "insights", to: "/insights", label: "Insights", Icon: Lightbulb, testid: "nav-insights", feature: "insights" },
    ],
  },
  {
    key: "admin", label: "Admin",
    items: [
      { key: "tasks", to: "/tasks", label: "Tareas", Icon: CheckSquare, testid: "nav-tasks" },
      { key: "hr", to: "/hr", label: "Recursos Humanos", Icon: UsersRound, testid: "nav-hr", feature: "hr_module" },
      { key: "bank_accounts", to: "/bank-accounts", label: "Mis Cuentas", Icon: Banknote, testid: "nav-bank-accounts", toggleable: false },
      { key: "import", to: "/import", label: "Importar Excel", Icon: FileSpreadsheet, testid: "nav-import", toggleable: false },
      { key: "company", to: "/company", label: "Mi Empresa", Icon: Building2, testid: "nav-company", toggleable: false },
      { key: "settings", to: "/settings", label: "Ajustes", Icon: Settings, testid: "nav-settings", toggleable: false },
    ],
  },
];

const COLLAPSE_STORAGE_KEY = "mn_sidebar_collapsed_v1";

function loadCollapsed() {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY) || "{}") || {};
  } catch { return {}; }
}

function saveCollapsed(state) {
  try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

const linkClass = ({ isActive }) =>
  `nav-link flex items-center gap-3 px-3 py-2 rounded-sm text-sm ${isActive ? "active" : ""}`;

function PlanBadge() {
  const { tier, sub } = useSubscription();
  const Icon = tier === "pro" ? Crown : tier === "premium" ? Zap : ShieldCheck;
  const isTrial = sub?.status === "trial";
  return (
    <Link
      to="/upgrade"
      data-testid="plan-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${tierBadgeClass(tier)} hover:opacity-80 transition-opacity`}
      title="Ver y cambiar plan"
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{tierLabel(tier)}</span>
      {isTrial && <Sparkles className="h-3 w-3" />}
    </Link>
  );
}

/** Renders one nav item; handles plan-locked state. */
function NavItem({ item, onClick }) {
  const { hasFeature } = useSubscription();
  const { to, label, Icon, testid, feature, end } = item;
  const locked = feature && !hasFeature(feature);
  if (locked) {
    return (
      <NavLink
        to={`/upgrade?reason=${feature}`}
        end={end}
        className="nav-link flex items-center gap-3 px-3 py-2 rounded-sm text-sm opacity-60 hover:opacity-100"
        data-testid={`${testid}-locked`}
        onClick={onClick}
        title="Disponible en plan superior"
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1">{label}</span>
        <Crown className="h-3.5 w-3.5 text-amber-500" />
      </NavLink>
    );
  }
  return (
    <NavLink to={to} end={end} className={linkClass} data-testid={testid} onClick={onClick}>
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

function SidebarNav({ modulos, onLinkClick }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => loadCollapsed());

  // Filter items by the per-tenant module preferences (toggleable defaults true)
  const visibleGroups = useMemo(() => NAV_GROUPS.map(g => ({
    ...g,
    items: g.items.filter(it => (it.toggleable === false) || modulos[it.key] !== false),
  })).filter(g => g.items.length > 0), [modulos]);

  // Auto-expand the group that contains the active route — even if the user
  // had previously collapsed it. We don't mutate localStorage so their explicit
  // choice survives navigating back out.
  const activeGroupKey = useMemo(() => {
    for (const g of visibleGroups) {
      if (g.items.some(it => location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to + "/")))) {
        return g.key;
      }
    }
    return null;
  }, [visibleGroups, location.pathname]);

  const isOpen = (key) => key === activeGroupKey ? true : !collapsed[key];

  const toggle = (key) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveCollapsed(next);
      return next;
    });
  };

  return (
    <nav className="space-y-3" data-testid="sidebar-nav">
      <NavItem item={TOP_LINK} onClick={onLinkClick} />

      {visibleGroups.map((group) => {
        const open = isOpen(group.key);
        return (
          <div key={group.key} className="space-y-0.5" data-testid={`sidebar-group-${group.key}`}>
            <button
              type="button"
              onClick={() => toggle(group.key)}
              aria-expanded={open}
              aria-controls={`sidebar-group-items-${group.key}`}
              className="w-full flex items-center justify-between px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 hover:text-slate-600 transition-colors"
              data-testid={`sidebar-group-toggle-${group.key}`}
            >
              <span>{group.label}</span>
              <ChevronDown
                className={`h-3 w-3 transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
                aria-hidden="true"
              />
            </button>
            <div
              id={`sidebar-group-items-${group.key}`}
              className="grid transition-[grid-template-rows] duration-200 ease-out"
              style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <div className="space-y-0.5">
                  {group.items.map(item => (
                    <NavItem key={item.key} item={item} onClick={onLinkClick} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="pt-2 border-t border-slate-200">
        <NavLink to="/upgrade" className={linkClass} data-testid="nav-upgrade" onClick={onLinkClick}>
          <Crown className="h-4 w-4 text-amber-500" />
          Plan & Precios
        </NavLink>
      </div>
    </nav>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // Default: every toggleable nav item is enabled. The preferences endpoint
  // can return a sparse map of overrides per tenant.
  const allItems = NAV_GROUPS.flatMap(g => g.items).filter(it => it.toggleable !== false);
  const defaultModulos = allItems.reduce((acc, it) => ({ ...acc, [it.key]: true }), {});
  const [modulos, setModulos] = useState(defaultModulos);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadPreferences = async () => {
    try {
      const res = await api.get("/preferences");
      setModulos(res.data.modulos || {});
    } catch {
      setModulos(defaultModulos);
    }
  };

  useEffect(() => {
    loadPreferences();
    const handler = () => loadPreferences();
    window.addEventListener("preferences-updated", handler);
    return () => window.removeEventListener("preferences-updated", handler);
  }, []);

  // Close mobile drawer whenever route changes
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // Reminder polling — every 60s check pending task reminders
  useEffect(() => {
    let cancelled = false;

    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    const checkReminders = async () => {
      try {
        const res = await api.get("/tasks/due-reminders");
        if (cancelled) return;
        for (const task of res.data) {
          const urgenciaIcon = task.urgencia === "alta" ? "🔴" : task.urgencia === "media" ? "🟡" : "🔵";
          toast(`${urgenciaIcon} Recordatorio: ${task.titulo}`, {
            description: task.descripcion || (task.fecha_vencimiento ? `Vence ${task.fecha_vencimiento}` : "Tarea pendiente"),
            duration: 10000,
            action: { label: "Ver", onClick: () => navigate("/tasks") },
          });
          if ("Notification" in window && Notification.permission === "granted") {
            try {
              new Notification(`Recordatorio: ${task.titulo}`, {
                body: task.descripcion || "Tarea pendiente en MiNegocio",
                tag: task.id,
              });
            } catch { /* ignore */ }
          }
          try { await api.post(`/tasks/${task.id}/mark-reminded`); } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    };

    checkReminders();
    const interval = setInterval(checkReminders, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F9F9FA]">
      {/* Top bar — fully responsive */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {/* Mobile hamburger (only < md) */}
              <Button
                data-testid="mobile-menu-button"
                variant="ghost"
                size="icon"
                className="md:hidden rounded-sm"
                onClick={() => setDrawerOpen(true)}
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h1
                className="text-xl sm:text-2xl font-black tracking-tight text-[#002FA7]"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                MiNegocio
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <PlanBadge />
              <NotificationBell role={user.role} />
              <span className="text-sm text-slate-600 hidden sm:inline" data-testid="topbar-user-name">
                {user.name}
              </span>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="rounded-sm"
                aria-label="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div
            className="drawer-overlay absolute inset-0 bg-slate-900/40"
            onClick={() => setDrawerOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside className="drawer-panel absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white border-r border-slate-200 shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h2
                className="text-lg font-black text-[#002FA7]"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                MiNegocio
              </h2>
              <Button
                data-testid="mobile-drawer-close"
                variant="ghost"
                size="icon"
                className="rounded-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Cerrar menú"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              <SidebarNav modulos={modulos} onLinkClick={() => setDrawerOpen(false)} />
            </div>
            <div className="px-4 py-3 border-t border-slate-200 text-xs text-slate-500">
              {user.name}
            </div>
          </aside>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex gap-6">
          {/* Desktop sidebar (md+) */}
          <aside className="hidden md:block w-56 flex-shrink-0">
            <div className="bg-white border border-slate-200 rounded-sm p-2 sticky top-20">
              <SidebarNav modulos={modulos} />
            </div>
          </aside>

          <main className="flex-1 min-w-0 fade-in">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
